import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import { useState, type FC } from 'react';
import BaseSelectBox from 'components/atoms/base-select-box';
import { ModuleType } from 'infrastructure/enums';
import { PlatformSelectOptions } from 'constants/platform';
import { useAssignCall } from 'infrastructure/hooks/untrackedInboundCalls/useAssignCall';
import PatientAutocomplete from 'components/molecules/patient-autocomplete';

import s from './style.module.scss';

import type { AssignCallRequest } from 'infrastructure/hooks/untrackedInboundCalls/types';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  callId: string;
  onSuccess: () => void;
}

const AssignCallModal: FC<Props> = ({
  closeModal,
  isOpen,
  callId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [callToAssign, setCallToAssign] = useState<AssignCallRequest>({
    callId,
    type: PlatformSelectOptions[0].value,
    guid: '',
  });

  const { mutate, isLoading: isAssigning } = useAssignCall({
    onSuccess,
  });

  return (
    <BaseModal
      bodyClassNames={s.modal}
      open={isOpen}
      onCancel={closeModal}
      width={1000}
      title={t('labels.untrackedInboundCalls')}
      loading={isAssigning}
      withFooter={[
        <BaseButton
          disabled={isAssigning}
          label={t('controls.cancel')}
          type="secondary"
          onClick={closeModal}
          key={t('controls.cancel')}
          dataCy="cancel-button"
        />,
        <BaseButton
          loading={isAssigning}
          disabled={!callToAssign.guid || !callToAssign.type}
          label={t('controls.save')}
          onClick={() => {
            mutate(callToAssign);
          }}
          key={t('controls.save')}
          dataCy="save-button"
        />,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span>{t('labels.patient')}</span>
        <PatientAutocomplete
          module={ModuleType.RPMCCM}
          onChange={(patientGuid) => {
            setCallToAssign({
              ...callToAssign,
              guid: patientGuid,
            });
          }}
        />
        <span>{t('labels.platform')}</span>
        <BaseSelectBox
          minWidth="200"
          label={t('labels.platform')}
          defaultValue={PlatformSelectOptions[0].value}
          allowClear={false}
          value={callToAssign.type}
          options={PlatformSelectOptions}
          onChange={(type) => {
            setCallToAssign({
              ...callToAssign,
              type,
            });
          }}
        />
      </div>
    </BaseModal>
  );
};

export default AssignCallModal;
